import React from "react";
import { useTranslation } from "react-i18next";
import { WebhookEndpointFilters } from "../../types/webhook";
import { useGetWebhooksQuery } from "../../store/api";

interface WebhookEndpointFiltersProps {
  filters: WebhookEndpointFilters;
  onFiltersChange: (filters: WebhookEndpointFilters) => void;
}

const WebhookEndpointFiltersComponent: React.FC<
  WebhookEndpointFiltersProps
> = ({ filters, onFiltersChange }) => {
  const { t } = useTranslation(["webhooks", "common"]);

  // Получаем список вебхуков для выбора
  const { data: webhooksData } = useGetWebhooksQuery({
    per_page: 100, // Получаем больше вебхуков для выбора
    sort_by: "name",
    sort_order: "asc",
  });

  const handleFilterChange = (
    key: keyof WebhookEndpointFilters,
    value: any
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t("webhooks:filters.search")}
          </label>
          <input
            type="text"
            value={filters.search || ""}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            placeholder={t("webhooks:filters.search_placeholder")}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t("webhooks:endpoints.filters.webhook")}
          </label>
          <select
            value={filters.webhook_id || ""}
            onChange={(e) => {
              const value = e.target.value;
              handleFilterChange(
                "webhook_id",
                value === "" ? undefined : parseInt(value)
              );
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t("common:all")}</option>
            {webhooksData?.data.map((webhook) => (
              <option key={webhook.id} value={webhook.id}>
                {webhook.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t("webhooks:filters.is_active")}
          </label>
          <select
            value={
              filters.is_active === undefined
                ? ""
                : filters.is_active.toString()
            }
            onChange={(e) => {
              const value = e.target.value;
              handleFilterChange(
                "is_active",
                value === "" ? undefined : value === "true"
              );
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t("common:all")}</option>
            <option value="true">{t("webhooks:status.active")}</option>
            <option value="false">{t("webhooks:status.inactive")}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t("webhooks:filters.sort_by")}
          </label>
          <select
            value={filters.sort_by || "created_at"}
            onChange={(e) => handleFilterChange("sort_by", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="created_at">{t("webhooks:table.created_at")}</option>
            <option value="name">{t("webhooks:table.name")}</option>
            <option value="priority">
              {t("webhooks:endpoints.table.priority")}
            </option>
            <option value="is_active">{t("webhooks:table.status")}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t("webhooks:filters.sort_order")}
          </label>
          <select
            value={filters.sort_order || "desc"}
            onChange={(e) =>
              handleFilterChange("sort_order", e.target.value as "asc" | "desc")
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="desc">{t("common:descending")}</option>
            <option value="asc">{t("common:ascending")}</option>
          </select>
        </div>
      </div>

      <div className="flex justify-between items-center mt-4">
        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("webhooks:filters.per_page")}
            </label>
            <select
              value={filters.per_page || 15}
              onChange={(e) =>
                handleFilterChange("per_page", parseInt(e.target.value))
              }
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        <button
          onClick={() =>
            onFiltersChange({
              webhook_id: undefined,
              is_active: undefined,
              search: "",
              sort_by: "created_at",
              sort_order: "desc",
              per_page: 15,
            })
          }
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:bg-gray-600 rounded-md transition-colors"
        >
          {t("common:clear")}
        </button>
      </div>
    </div>
  );
};

export default WebhookEndpointFiltersComponent;
